import React from 'react';
import { BotDifficulty, GameConfig, HandSize } from '../core/types';
import { soundManager } from '../utils/sound';
import '../styles/Modals.css';

interface SettingsModalProps {
  config: GameConfig;
  isManualArrange: boolean;
  onToggleArrangeMode: () => void;
  onUpdateConfig: (newConfig: GameConfig) => void;
  onClose: () => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({
  config,
  isManualArrange,
  onToggleArrangeMode,
  onUpdateConfig,
  onClose
}) => {
  const handleSoundToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = e.target.checked;
    soundManager.setEnabled(enabled);
    onUpdateConfig({ ...config, soundEnabled: enabled });
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const vol = parseFloat(e.target.value);
    soundManager.setVolume(vol);
    if (vol > 0) soundManager.playClick();
  };

  const handleDifficultyChange = (diff: BotDifficulty) => {
    onUpdateConfig({ ...config, botDifficulty: diff });
    soundManager.playClick();
  };

  const handleRoundsChange = (rounds: number) => {
    onUpdateConfig({ ...config, totalRounds: rounds });
    soundManager.playClick();
  };

  const handleHandSizeChange = (handSize: HandSize) => {
    const minHuxiToWin = handSize === 21
      ? Math.max(config.minHuxiToWin, 10)
      : config.minHuxiToWin === 15 ? 10 : config.minHuxiToWin;
    onUpdateConfig({ ...config, handSize, minHuxiToWin });
    soundManager.playClick();
  };

  const handleMinHuxiChange = (minHuxiToWin: number) => {
    onUpdateConfig({ ...config, minHuxiToWin });
    soundManager.playClick();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content settings-modal">
        <div className="modal-header">
          <h3>⚙️ 牌局设置</h3>
          <button className="modal-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="settings-body">
          <div className="setting-group">
            <h4>🀄 牌数与起胡</h4>
            <div className="setting-row">
              <span>牌数</span>
              <div className="rounds-buttons">
                {[21, 15].map(size => (
                  <button
                    key={size}
                    className={`btn-round-select ${config.handSize === size ? 'active' : ''}`}
                    onClick={() => handleHandSizeChange(size as HandSize)}
                  >
                    {size} 张
                  </button>
                ))}
              </div>
            </div>
            <div className="setting-row">
              <span>胡牌要求</span>
              <div className="rounds-buttons">
                {(config.handSize === 21 ? [15, 10] : [10, 6]).map(minHuxi => (
                  <button
                    key={minHuxi}
                    className={`btn-round-select ${config.minHuxiToWin === minHuxi ? 'active' : ''}`}
                    onClick={() => handleMinHuxiChange(minHuxi)}
                  >
                    {minHuxi} 胡
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="setting-group">
            <h4>🀄 手牌排列</h4>
            <div className="setting-row">
              <span>{isManualArrange ? '手动排列' : '自动排列'}</span>
              <button
                className={`arrange-mode-toggle ${isManualArrange ? 'manual' : ''}`}
                onClick={onToggleArrangeMode}
              >
                {isManualArrange ? '切换为自动' : '切换为手动'}
              </button>
            </div>
          </div>

          {/* 音效与语音设置 */}
          <div className="setting-group">
            <h4>🔊 声音与音效</h4>
            <div className="setting-row">
              <span>开启音效 & 语音报牌</span>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={soundManager.isEnabled()}
                  onChange={handleSoundToggle}
                />
                <span className="slider"></span>
              </label>
            </div>
            <div className="setting-row">
              <span>音量调节</span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                defaultValue={soundManager.getVolume()}
                onChange={handleVolumeChange}
                className="volume-slider"
              />
            </div>
          </div>

          {/* AI 对手难度 */}
          <div className="setting-group">
            <h4>🤖 机器人对手水平</h4>
            <div className="difficulty-buttons">
              <button
                className={`btn-diff ${config.botDifficulty === BotDifficulty.EASY ? 'active' : ''}`}
                onClick={() => handleDifficultyChange(BotDifficulty.EASY)}
              >
                🐣 休闲菜鸟
              </button>
              <button
                className={`btn-diff ${config.botDifficulty === BotDifficulty.MEDIUM ? 'active' : ''}`}
                onClick={() => handleDifficultyChange(BotDifficulty.MEDIUM)}
              >
                🧔 市井老手
              </button>
              <button
                className={`btn-diff ${config.botDifficulty === BotDifficulty.HARD ? 'active' : ''}`}
                onClick={() => handleDifficultyChange(BotDifficulty.HARD)}
              >
                👑 雀圣大师
              </button>
            </div>
          </div>

          {/* 比赛局数 */}
          <div className="setting-group">
            <h4>🏁 比赛总局数</h4>
            <div className="rounds-buttons">
              {[4, 8, 12].map(num => (
                <button
                  key={num}
                  className={`btn-round-select ${config.totalRounds === num ? 'active' : ''}`}
                  onClick={() => handleRoundsChange(num)}
                >
                  {num} 局对决
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-primary" onClick={onClose}>
            保存并返回
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;